import type { ParentGameSummary, RewardResult, SkillSummary } from '../../sdk';
import { INVENTED_CAMERA_ACTIVITY_COPY } from '../../platform/config/camera-activity';
import type { SittingCameraQuality } from '../routes/game-host/camera-activity';

/**
 * The parent summary after a mission (game design spec §33, MVP notes §21).
 *
 * Two rules shape every line of this screen.
 *
 * **Non-competitive invariant (architecture §12, COOP-07).** There is one shared team score and
 * it is never split, attributed or compared. Players are listed in the order the session held
 * them, with neutral participation counts and no per-player score to sort by. Nothing here says
 * who was first, faster, better or behind, because nothing here knows — and the moment a screen
 * like this learns, siblings stop playing together.
 *
 * **Vocabulary register (game design spec §33).** "Practised", "growing", "ready for more" —
 * never "failed", "weak", "poor", "behind", "lost". An unfinished mission is described as a
 * place to carry on from, which is what it is.
 *
 * The component renders whatever the game's own `ParentSummaryAdapter` produced; it computes
 * nothing about learning itself. The one derived line, "practised most", is exactly what its
 * name says — the highest practice count — and not a claim about mastery, which today's evidence
 * cannot support.
 */

export interface ParentSummaryViewProps {
  summary: ParentGameSummary;
  /** The reward the runtime computed for this session, if the mission earned one. */
  reward?: RewardResult;
  /**
   * What the camera managed, **for the sitting** (MIN §21, GDS §33, D-076(6), Slice G′).
   *
   * Absent for a keyboard sitting, which then reads exactly as it did before this prop existed.
   *
   * It arrives from the platform rather than from the game's `ParentSummaryAdapter` on purpose:
   * camera quality is a fact about the platform's own detectors, measured by the platform's own
   * CAM-08 counters, and routing it through the game module would make a game responsible for
   * reporting on hardware it is not allowed to see.
   */
  cameraQuality?: SittingCameraQuality;
  /**
   * How many times a grown-up used a mid-mission override this sitting (PS6-9, D-106).
   *
   * A fact about the **sitting**, never about a child: it sits outside the participation list for
   * exactly the reason the camera figure does — inside it, any number is read as belonging to the
   * child whose row it is in. Undefined means nothing was counted, which is not the same as zero
   * and is therefore shown as nothing at all.
   */
  parentOverridesUsed?: number;
  onPlayAgain: () => void;
  onLeave: () => void;
}

function formatDuration(durationMs: number): string {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1_000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds} sec`;
  return `${minutes} min ${seconds} sec`;
}

/** Highest practice count, first-listed on a tie. Deliberately not a "strongest skill" claim. */
function practisedMost(skills: readonly SkillSummary[]): SkillSummary | undefined {
  return skills.reduce<SkillSummary | undefined>(
    (best, skill) =>
      best === undefined || skill.practisedCount > best.practisedCount ? skill : best,
    undefined,
  );
}

export function ParentSummaryView({
  summary,
  reward,
  cameraQuality,
  parentOverridesUsed,
  onPlayAgain,
  onLeave,
}: ParentSummaryViewProps) {
  const shared = summary.sharedResult;
  const most = practisedMost(summary.skillsPractised);

  return (
    <section data-testid="parent-summary">
      <h1>How the mission went</h1>
      <p className="lede">{summary.title}</p>

      {shared === undefined ? null : (
        <div className="panel" data-testid="summary-shared-result">
          <h2 style={{ marginTop: 0 }}>Together</h2>
          <p className="lede">
            {shared.completed
              ? 'The mission is finished — every number portal is working again.'
              : 'The mission is still part way through, ready to carry on next time.'}
          </p>
          <ul>
            <li>Number shards collected together: {shared.teamScore}</li>
            <li>Questions tried: {shared.questionsAttempted}</li>
            <li>Questions finished: {shared.questionsCompleted}</li>
            <li>Extra tries taken: {shared.retryCount}</li>
            <li>Time playing: {formatDuration(shared.durationMs)}</li>
          </ul>
        </div>
      )}

      {reward === undefined ? null : (
        <div className="panel" data-testid="summary-reward">
          <h2 style={{ marginTop: 0 }}>Earned this time</h2>
          <p className="lede">
            <span className="badge">{reward.label}</span>
          </p>
          {reward.message === undefined ? null : <p>{reward.message}</p>}
        </div>
      )}

      <h2>Who joined in</h2>
      <ul className="player-list" data-testid="summary-participation">
        {summary.playerSummaries.map((player) => (
          <li key={player.playerId}>
            <strong>{player.label}</strong>
            <span>
              {' '}
              answered {player.responsesObserved}{' '}
              {player.responsesObserved === 1 ? 'time' : 'times'}
              {player.participationWithinGrace > 0
                ? `, and joined in on ${player.participationWithinGrace} more`
                : ''}
              .
            </span>
            {player.bothRequiredCompletions > 0 ? (
              <span data-testid="summary-team-moments">
                {' '}
                Took part in {player.bothRequiredCompletions}{' '}
                {player.bothRequiredCompletions === 1 ? 'team moment' : 'team moments'}.
              </span>
            ) : null}
            {/*
             * There was a per-child "Camera reading quality" figure here until Slice G′.
             *
             * `PlayerGameSummary.inputQuality` is a slot in `src/sdk` that no adapter has ever
             * populated, so nothing was ever shown — but the moment a two-child sitting populated
             * it, this screen would have put two children's input-quality readings one above the
             * other, which is the sibling comparison D-076(6) forbids however neutrally it is
             * worded. Camera quality is reported once, for the sitting, below.
             */}
          </li>
        ))}
      </ul>

      {cameraQuality === undefined ? null : (
        /**
         * One figure for the sitting (D-076(6)).
         *
         * It sits outside the participation list deliberately: inside it, any camera figure is
         * read as belonging to the child whose row it is in.
         */
        <div
          className="panel"
          data-testid="summary-camera-quality"
          data-coverage={cameraQuality.coverage}
          data-interruptions={String(cameraQuality.interruptions)}
          data-recognised-share={cameraQuality.recognisedShare?.toFixed(2) ?? ''}
        >
          <h2 style={{ marginTop: 0 }}>{INVENTED_CAMERA_ACTIVITY_COPY.summaryHeading}</h2>
          <ul>
            <li>
              {INVENTED_CAMERA_ACTIVITY_COPY.shareLabel}:{' '}
              {cameraQuality.recognisedShare === null
                ? INVENTED_CAMERA_ACTIVITY_COPY.shareUnavailable
                : `${Math.round(cameraQuality.recognisedShare * 100)}%`}
            </li>
            <li>
              {INVENTED_CAMERA_ACTIVITY_COPY.interruptionsLabel}: {cameraQuality.interruptions}
            </li>
          </ul>
          <p className="notice">
            {cameraQuality.coverage === 'CAMERA_HEROES_ONLY'
              ? INVENTED_CAMERA_ACTIVITY_COPY.coverageCameraHeroesOnly
              : INVENTED_CAMERA_ACTIVITY_COPY.coverageEveryHero}
          </p>
          <p className="notice">{INVENTED_CAMERA_ACTIVITY_COPY.bound}</p>
        </div>
      )}

      {parentOverridesUsed === undefined || parentOverridesUsed === 0 ? null : (
        /**
         * Neutral, aggregate, and about the evening (PS6-9).
         *
         * Named as help given rather than as help needed, and attached to nobody: "the grown-up
         * stepped in twice" is an observation a family can use, and it stays one only for as long
         * as it is not filed under a child's name. Nothing is shown when the controls were never
         * used — a zero here reads as a score.
         *
         * COPY FLAG (grown-up strings).
         */
        <p
          className="notice"
          data-testid="summary-parent-overrides"
          data-parent-overrides={String(parentOverridesUsed)}
        >
          A grown-up helped from the controls {parentOverridesUsed}{' '}
          {parentOverridesUsed === 1 ? 'time' : 'times'} this sitting.
        </p>
      )}

      <h2>Practised today</h2>
      {summary.skillsPractised.length === 0 ? (
        <p className="notice">No questions were answered this time.</p>
      ) : (
        <ul data-testid="summary-skills">
          {summary.skillsPractised.map((skill) => (
            <li key={skill.skillId}>
              {skill.label} — practised {skill.practisedCount}{' '}
              {skill.practisedCount === 1 ? 'time' : 'times'}
            </li>
          ))}
        </ul>
      )}

      {most === undefined ? null : (
        <p className="notice" data-testid="summary-practised-most">
          Practised most: {most.label}.
        </p>
      )}

      {summary.nextSuggestions.length === 0 ? null : (
        <>
          <h2>Ready for more</h2>
          <ul data-testid="summary-suggestions">
            {summary.nextSuggestions.map((suggestion) => (
              <li key={suggestion.id}>{suggestion.text}</li>
            ))}
          </ul>
        </>
      )}

      <p>
        <button type="button" className="button button--primary" onClick={onPlayAgain}>
          Play again
        </button>{' '}
        <button type="button" className="button button--quiet" onClick={onLeave}>
          Finish for now
        </button>
      </p>
    </section>
  );
}
